// Sign in and sign up. An account is what owns a room link, hosts it, keeps
// call history and recordings, and sets the defaults below; a guest never
// needs one to join.

import { useState, type FormEvent, type JSX } from "react";
import { suggestHandle, type AccountUser, type AuthResponse } from "@breeze/core";
import { api, ApiError } from "../lib/api";
import { linkHandler, navigate } from "../lib/nav";
import { ownRoomPath, rememberUserKey, setUserKey } from "../lib/storage";

interface AuthScreenProps {
  readonly mode: "signin" | "signup";
  readonly onSignedIn: (user: AccountUser) => void;
}

type Field = "name" | "handle" | "email" | "password";

export function AuthScreen({ mode, onSignedIn }: AuthScreenProps): JSX.Element {
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [handleEdited, setHandleEdited] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field: Field | null; message: string } | null>(null);

  const signingUp = mode === "signup";
  const shownHandle = handleEdited ? handle : suggestHandle(name);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = signingUp
        ? await api<AuthResponse>("/api/auth/signup", { method: "POST", body: { name, handle: shownHandle, email, password } })
        : await api<AuthResponse>("/api/auth/signin", { method: "POST", body: { email, password } });
      setUserKey(res.key);
      rememberUserKey();
      onSignedIn(res.user);
      navigate(ownRoomPath(), { replace: true });
    } catch (err) {
      const e = err instanceof ApiError ? err : new ApiError(0, null);
      setError({ field: e.field ?? null, message: e.message });
      setBusy(false);
    }
  };

  const fieldError = (field: Field): JSX.Element | null =>
    error?.field === field ? <p className="field__error">{error.message}</p> : null;
  const inputClass = (field: Field): string => (error?.field === field ? "input input--bad" : "input");

  return (
    <div className="auth">
    <form className="auth__card" onSubmit={(e) => void submit(e)} noValidate>
      <h1 className="auth__title">{signingUp ? "Make an account" : "Sign in"}</h1>

      {signingUp ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="auth-name">
              Your name
            </label>
            <input
              id="auth-name"
              className={inputClass("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              maxLength={80}
              autoFocus
            />
            {fieldError("name")}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="auth-handle">
              Your link
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="muted" style={{ fontSize: 15, flex: "none" }}>
                {window.location.host}/
              </span>
              <input
                id="auth-handle"
                className={inputClass("handle")}
                value={shownHandle}
                onChange={(e) => {
                  setHandleEdited(true);
                  setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                }}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={30}
              />
            </div>
            {fieldError("handle")}
          </div>
        </>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="auth-email">
          Email
        </label>
        <input
          id="auth-email"
          className={inputClass("email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          autoFocus={!signingUp}
        />
        {fieldError("email")}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="auth-password">
          Password
        </label>
        <input
          id="auth-password"
          className={inputClass("password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={signingUp ? "new-password" : "current-password"}
        />
        {fieldError("password")}
      </div>

      {error && error.field === null ? <div className="notice notice--danger">{error.message}</div> : null}

      <button type="submit" className="btn btn--primary auth__submit" disabled={busy}>
        {busy ? (signingUp ? "Making it…" : "Signing in…") : signingUp ? "Make account" : "Sign in"}
      </button>

      <p className="muted" style={{ margin: 0, fontSize: 14.5, textAlign: "center" }}>
        {signingUp ? (
          <>
            Have an account?{" "}
            <a className="textlink" href="/signin" onClick={linkHandler("/signin")}>
              Sign in
            </a>
          </>
        ) : (
          <>
            New here?{" "}
            <a className="textlink" href="/signup" onClick={linkHandler("/signup")}>
              Make an account
            </a>
          </>
        )}
      </p>
    </form>

      <p className="muted auth__legal">
        <a href="/privacy" onClick={linkHandler("/privacy")} style={{ textDecoration: "none" }}>
          Privacy
        </a>
        {"  ·  "}
        <a href="/terms" onClick={linkHandler("/terms")} style={{ textDecoration: "none" }}>
          Terms
        </a>
      </p>
    </div>
  );
}
